import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const backgroundImageUrl = `${process.env.PUBLIC_URL}/images/login-bg.png`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setSent(true);
      toast.success('Check your email for the reset link');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={{ ...styles.bgImage, backgroundImage: `url(${backgroundImageUrl})` }} />
      <div style={styles.overlay} />

      <div style={styles.cardOuter}>
        <div style={styles.card}>
          {/* WTI Logo */}
          <div style={styles.logoSection}>
            <div style={styles.brandRow}>
              <span style={styles.brandLetter}>w</span>
              <span style={styles.brandLetter}>t</span>
              <span style={styles.brandLetterI}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/logo.png`}
                  alt="WTI"
                  style={styles.logoPin}
                />
                <span style={styles.dotlessI}>ı</span>
              </span>
            </div>
            <div style={styles.brandSubtext}>where to invest</div>
          </div>

          {!sent ? (
            <>
              <p style={styles.heading}>Reset your password</p>
              <p style={styles.subHeading}>
                Enter the email address associated with your account and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit}>
                <div style={styles.inputGroup}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address*"
                    required
                    style={styles.input}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#1a73e8';
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(26,115,232,0.15)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#dadce0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{ ...styles.continueBtn, opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Success state */}
              <div style={styles.successIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <p style={styles.heading}>Check your email</p>
              <p style={styles.subHeading}>
                If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly. Check your inbox and spam folder.
              </p>

              <button
                onClick={() => { setSent(false); setEmail(''); }}
                style={{ ...styles.continueBtn, marginTop: '20px' }}
              >
                Try another email
              </button>
            </>
          )}

          <p style={styles.bottomRow}>
            Remember your password?{' '}
            <Link to="/login" style={styles.link}>Back to login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    minHeight: '100vh',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bgImage: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    zIndex: 0,
  },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1,
  },
  cardOuter: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '440px',
    padding: '24px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #1a73e8',
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    padding: '48px 40px 36px',
  },
  logoSection: {
    textAlign: 'center' as const,
    marginBottom: '24px',
  },
  brandRow: {
    display: 'inline-flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    lineHeight: 1,
  },
  brandLetter: {
    fontSize: '48px',
    fontWeight: 900,
    color: '#222',
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    lineHeight: 1,
  },
  brandLetterI: {
    fontSize: '48px',
    fontWeight: 900,
    color: '#222',
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    position: 'relative' as const,
    lineHeight: 1,
    display: 'inline-block',
  },
  dotlessI: {
    fontSize: '48px',
    fontWeight: 900,
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    lineHeight: 1,
  },
  logoPin: {
    position: 'absolute' as const,
    top: '-6px',
    left: '50%',
    transform: 'translateX(-50%)',
    height: '28px',
    width: 'auto',
  },
  brandSubtext: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1a73e8',
    letterSpacing: '0.5px',
    marginTop: '4px',
  },
  heading: {
    textAlign: 'center' as const,
    fontSize: '16px',
    color: '#3c4043',
    fontWeight: 400,
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  subHeading: {
    textAlign: 'center' as const,
    fontSize: '14px',
    color: '#5f6368',
    marginBottom: '28px',
    lineHeight: '1.6',
  },
  successIcon: {
    textAlign: 'center' as const,
    marginBottom: '16px',
  },
  inputGroup: {
    marginBottom: '24px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: '1px solid #dadce0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#fff',
    color: '#202124',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box' as const,
  },
  continueBtn: {
    width: '100%',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#1a73e8',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    letterSpacing: '0.3px',
  },
  devBox: {
    backgroundColor: '#f0f7ff',
    border: '1px solid #d2e3fc',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '16px',
  },
  bottomRow: {
    marginTop: '24px',
    textAlign: 'center' as const,
    fontSize: '14px',
    color: '#5f6368',
  },
  link: {
    color: '#1a73e8',
    fontWeight: 600,
    textDecoration: 'none',
  },
};

export default ForgotPassword;
